import { Clock, Info } from "lucide-react"

import { Avatar } from "@/components/tuw/Avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { briefCopy } from "@/lib/openHouse/brief"
import { REQUEST_WORDS, requestCopy } from "@/lib/openHouse/requests"

const STRIP =
  "mb-[14px] flex flex-wrap items-center gap-x-[11px] gap-y-2 rounded-xl border px-[18px] py-3 text-[13px] text-foreground max-[560px]:px-4"

// The order page's Open House requests, after a request is accepted
// (OrderDetailPresenter `requestOutcome`, nil when there is nothing to say):
// waiting on the freelancer's confirmation (new-flow-wording §10), the
// freelancer declined or never confirmed with the way back to requests (§11),
// an invited freelancer waiting on the brief (§9), or no requests yet (§13).
export default function RequestOutcomeStrip({ outcome, waiting = 0, onReview }) {
  if (!outcome) return null

  const copy = requestCopy(outcome.kind)

  if (outcome.state === "awaiting" || outcome.state === "inviteWaits") {
    const awaiting = outcome.state === "awaiting"
    return (
      <div role="note" data-testid="request-outcome" className={`${STRIP} border-border bg-card`}>
        <Avatar initials={outcome.freelancer?.initials} name={outcome.freelancer?.name} size={30} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display font-bold">{outcome.freelancer?.name}</span>
            {awaiting && <Badge variant="progress">{REQUEST_WORDS.accepted}</Badge>}
          </div>
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Clock className="size-3.5 shrink-0" strokeWidth={2} />
            {awaiting ? copy.awaitConfirm : copy.inviteWaits}
          </span>
        </div>
      </div>
    )
  }

  if (outcome.state === "declined" || outcome.state === "unconfirmed") {
    return (
      <div role="note" data-testid="request-outcome" className={`${STRIP} border-needs-you/35 bg-needs-you/10`}>
        <Info className="size-4 shrink-0 text-needs-you" strokeWidth={1.9} />
        <span className="min-w-0 flex-1">{outcome.state === "declined" ? copy.fDeclined : copy.fUnconfirmed}</span>
        {waiting > 0 ? (
          <Button size="sm" className="font-semibold max-[560px]:w-full" onClick={onReview}>
            {/* The cover brief's own "back" leaves its page; here it opens the requests, by their heading. */}
            {outcome.kind === "cover" ? copy.requests : briefCopy(outcome.kind).back}
          </Button>
        ) : (
          <span className="text-muted-foreground">{REQUEST_WORDS.noRequests}</span>
        )}
      </div>
    )
  }

  return (
    <p role="note" data-testid="request-outcome" className="mb-[14px] text-[13px] text-muted-foreground">
      {REQUEST_WORDS.noRequests}
    </p>
  )
}
